import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { companiesApi } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Building2, Users, Pencil, Trash2, Mail, Phone, Link, Copy, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await companiesApi.getAll();
      setCompanies(response.data);
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (company = null) => {
    if (company) {
      setSelectedCompany(company);
      setFormData({
        name: company.name,
        contact_email: company.contact_email || '',
        contact_phone: company.contact_phone || '',
      });
    } else {
      setSelectedCompany(null);
      setFormData({ name: '', contact_email: '', contact_phone: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCompany) {
        await companiesApi.update(selectedCompany.id, formData);
        toast.success('Company updated successfully');
      } else {
        await companiesApi.create(formData);
        toast.success('Company created successfully');
      }
      setDialogOpen(false);
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const handleDelete = async () => {
    if (!selectedCompany) return;
    try {
      await companiesApi.delete(selectedCompany.id);
      toast.success('Company deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
      loadCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const handleOpenLinkDialog = (company) => {
    setSelectedCompany(company);
    setLinkDialogOpen(true);
  };

  const getUploadUrl = () => {
    if (!selectedCompany?.upload_token) return '';
    return `${window.location.origin}/upload/${selectedCompany.upload_token}`;
  };

  const copyUploadLink = () => {
    navigator.clipboard.writeText(getUploadUrl());
    toast.success('Upload link copied to clipboard!');
  };

  const regenerateToken = async () => {
    if (!selectedCompany) return;
    try {
      const response = await api.post(`/companies/${selectedCompany.id}/regenerate-token`);
      setSelectedCompany({...selectedCompany, upload_token: response.data.upload_token});
      loadCompanies();
      toast.success('New upload link generated');
    } catch (error) {
      toast.error('Failed to regenerate link');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Companies</h1>
          <p className="text-muted-foreground mt-1">
            Manage employer companies in your census
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#E31837] hover:bg-[#c41530] text-white"
          data-testid="add-company-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Companies Grid */}
      {companies.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12">
            <div className="empty-state">
              <Building2 className="w-12 h-12 mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-medium mb-2">No companies yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add your first employer company to start managing employee census data.
              </p>
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-[#E31837] hover:bg-[#c41530] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Company
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {companies.map((company) => (
            <motion.div key={company.id} variants={itemVariants}>
              <Card
                className="bg-card border-border hover:border-primary/50 transition-colors"
                data-testid={`company-card-${company.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{company.name}</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenLinkDialog(company)}
                        title="Get Upload Link"
                        data-testid={`upload-link-${company.id}`}
                      >
                        <Link className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(company)}
                        data-testid={`edit-company-${company.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedCompany(company);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-company-${company.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {company.employee_count} employees
                    </span>
                  </div>
                  {company.contact_email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground truncate">
                        {company.contact_email}
                      </span>
                    </div>
                  )}
                  {company.contact_phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {company.contact_phone}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedCompany ? 'Edit Company' : 'Add Company'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name *</Label>
              <Input
                id="name"
                data-testid="company-name-input"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Acme Corporation"
                required
                className="bg-background border-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                data-testid="company-email-input"
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  setFormData({ ...formData, contact_email: e.target.value })
                }
                placeholder="hr@company.com"
                className="bg-background border-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                data-testid="company-phone-input"
                value={formData.contact_phone}
                onChange={(e) =>
                  setFormData({ ...formData, contact_phone: e.target.value })
                }
                placeholder="(555) 123-4567"
                className="bg-background border-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#E31837] hover:bg-[#c41530] text-white"
                data-testid="save-company-button"
              >
                {selectedCompany ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedCompany?.name}"? This will
              also mark all associated employees as inactive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="w-5 h-5 text-blue-500" />
              Secure Upload Link
            </DialogTitle>
            <DialogDescription>
              Share this link with {selectedCompany?.name} to allow them to upload census data securely.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <Label className="text-xs text-muted-foreground mb-2 block">Upload URL</Label>
              <div className="flex gap-2">
                <Input
                  value={getUploadUrl()}
                  readOnly
                  className="bg-background font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyUploadLink}
                  title="Copy to clipboard"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="flex items-center gap-2 text-sm">
                <RefreshCw className="w-4 h-4 text-yellow-500" />
                <span>Regenerate link to revoke previous access</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={regenerateToken}
                className="text-yellow-500 border-yellow-500/30"
              >
                Regenerate
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setLinkDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
